import { useCRUDModalList } from '@/shared/hooks/useCRUDModalList';
import { useServiceStatus } from '@/contexts/service-status';
import { propertyTypeService } from '@/services/propertyType.service';
import type { PropertyType, CreatePropertyTypeRequest, UpdatePropertyTypeRequest } from '@/services/types/user.types';

const propertyTypeServiceAdapter = {
  getAll: () => propertyTypeService.getAllPropertyTypes(),
  create: (data: CreatePropertyTypeRequest) => propertyTypeService.createPropertyType(data) as Promise<PropertyType>,
  update: (id: string, data: UpdatePropertyTypeRequest) => propertyTypeService.updatePropertyType(id, data).then(() => ({} as PropertyType)),
  delete: (id: string) => propertyTypeService.deletePropertyType(id),
};

export function usePropertyTypeList() {
  const { isServiceOnline } = useServiceStatus();
  const isUserServiceOnline = isServiceOnline('user');

  const {
    items: propertyTypes,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew,
    handleEdit,
    handleDelete,
    handleModalCancel,
    handleModalSubmit,
  } = useCRUDModalList<PropertyType, CreatePropertyTypeRequest, UpdatePropertyTypeRequest>({
    service: propertyTypeServiceAdapter,
    entityName: 'tipo de imóvel',
    enabled: isUserServiceOnline,
    mapToFormValues: (item) => ({
      name: item.name,
      code: item.code,
      description: item.description,
      isActive: item.isActive,
    }),
    mapFromFormValues: (values, isEditing) => {
      if (isEditing) {
        return {
          name: values.name as string,
          code: values.code as string,
          description: (values.description as string) || '',
          isActive: (values.isActive as boolean) ?? true,
        };
      }
      return {
        name: values.name as string,
        code: values.code as string,
        description: (values.description as string) || '',
      };
    },
    messages: {
      loadError: 'Erro ao carregar tipos de imóvel',
      createSuccess: 'Tipo de imóvel criado com sucesso!',
      updateSuccess: 'Tipo de imóvel atualizado com sucesso!',
      deleteSuccess: 'Tipo de imóvel excluído com sucesso!',
      deleteError: 'Erro ao excluir tipo de imóvel',
      saveError: 'Erro ao salvar tipo de imóvel',
      deleteConfirmContent: 'Tem certeza que deseja remover este tipo de imóvel? Esta ação não pode ser desfeita.',
    },
  });

  const handleNameChange = (name: string) => {
    if (!editingItem) {
      const code = name.substring(0, 3).toUpperCase();
      form.setFieldValue('code', code);
    }
  };

  return {
    propertyTypes,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew,
    handleEdit,
    handleDelete,
    handleNameChange,
    handleModalCancel,
    handleModalSubmit,
  };
}
